// The detail panes of the Activity view: a whole conversation (Transcript)
// and any other single event (EventDetail).
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMicrophone, faTriangleExclamation, faClock, faWrench, faBolt, faFilePowerpoint, faFilePdf, faFileWord,
  faEnvelopeOpenText, faChartColumn, faFileLines, faDatabase, faRobot, faCircleStop,
} from '@fortawesome/free-solid-svg-icons';
import { alignNumericColumns } from '../utils/markdownTables';
import {
  areaLabel, dateTime, describe, docLabel, duration, fullName, hue, initials, num, timeOf, CATEGORY,
} from './format';

export function Avatar({ user, size = 32 }) {
  const h = hue(user?.username);
  return (
    <span className="ac-avatar" style={{ width: size, height: size, fontSize: size * 0.38, '--ac-hue': h }}>
      {initials(user)}
    </span>
  );
}

const OUT_ICON = { powerpoint: faFilePowerpoint, pdf: faFilePdf, word: faFileWord, email: faEnvelopeOpenText, chart: faChartColumn };

function Outputs({ outputs }) {
  if (!outputs?.length) return null;
  return (
    <div className="ac-outputs">
      {outputs.map((o, i) => (
        <span key={i} className="ac-output">
          <FontAwesomeIcon icon={OUT_ICON[o.type] || faFileLines} /> {docLabel(o.type)}{o.title ? `: ${o.title}` : ''}
        </span>
      ))}
    </div>
  );
}

function Tools({ tools }) {
  if (!tools?.length) return null;
  const counted = [];
  tools.forEach((t) => {
    const name = t.view ? `${t.tool} · ${t.view}` : t.tool;
    const hit = counted.find((c) => c.name === name);
    if (hit) { hit.n += 1; hit.error = hit.error || t.error; } else counted.push({ name, n: 1, error: t.error, args: t.args });
  });
  return (
    <div className="ac-tools">
      <FontAwesomeIcon icon={faWrench} />
      {counted.map((c) => (
        <span key={c.name} className={`ac-tool${c.error ? ' error' : ''}`} title={c.args}>{c.name}{c.n > 1 ? ` ×${c.n}` : ''}</span>
      ))}
    </div>
  );
}

function Turn({ e }) {
  const d = e.detail || {};
  const stats = d.stats || {};
  return (
    <article className={`ac-turn${e.status === 'error' || e.status === 'aborted' ? ' error' : ''}`}>
      <div className="ac-q">
        <div className="ac-q-meta">
          <span className="ac-q-who">{e.username}</span>
          <span>{timeOf(e.at)}</span>
          {d.channel === 'voice' && <span className="ac-pill"><FontAwesomeIcon icon={faMicrophone} /> voice</span>}
          {d.turn > 1 && <span className="ac-muted">turn {d.turn}</span>}
        </div>
        <div className="ac-q-text">{d.question || e.title}</div>
      </div>
      <div className="ac-a">
        <div className="ac-a-head">
          <span className="ac-a-icon"><FontAwesomeIcon icon={faRobot} /></span>
          <b>{areaLabel(e.area)}</b>
          {d.data_set && <span className="ac-pill"><FontAwesomeIcon icon={faDatabase} /> {d.data_set}</span>}
          <span className="ac-a-stats">
            <span><FontAwesomeIcon icon={faClock} /> {duration(e.duration_ms)}</span>
            {e.tokens ? <span><FontAwesomeIcon icon={faBolt} /> {num(e.tokens)} tokens</span> : null}
            {stats.llm_calls ? <span>{stats.llm_calls} model calls</span> : null}
          </span>
        </div>
        {d.error && (
          <div className="ac-alert"><FontAwesomeIcon icon={faTriangleExclamation} /> {d.error}</div>
        )}
        {d.aborted && (
          <div className="ac-alert soft"><FontAwesomeIcon icon={faCircleStop} /> Broken off before the answer was complete — the browser closed or the connection dropped.</div>
        )}
        {d.answer ? (
          <div className="ac-md">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{alignNumericColumns(d.answer)}</ReactMarkdown>
          </div>
        ) : !d.error && <div className="ac-muted">No answer text.</div>}
        <Outputs outputs={d.outputs} />
        <Tools tools={d.tools} />
      </div>
    </article>
  );
}

export function Transcript({ client, threadKey, onPerson }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let off = false;
    setData(null);
    setError('');
    client.get(`/api/admin/activity/thread?key=${encodeURIComponent(threadKey)}`)
      .then((v) => { if (!off) setData(v); })
      .catch((e) => { if (!off) setError(e.message); });
    return () => { off = true; };
  }, [client, threadKey]);

  if (error) return <div className="ac-empty">{error}</div>;
  if (!data) return <div className="ac-empty"><span className="ac-spinner" /></div>;
  const turns = data.events.filter((e) => e.kind === 'chat');
  const side = data.events.filter((e) => e.kind !== 'chat' && e.kind !== 'document');
  const first = turns[0] || data.events[0];
  const user = { username: first.username };
  const total = turns.reduce((s, e) => s + (e.duration_ms || 0), 0);
  const tokens = turns.reduce((s, e) => s + (e.tokens || 0), 0);
  const ds = turns.find((t) => t.detail?.data_set)?.detail;
  return (
    <div className="ac-transcript">
      <header className="ac-transcript-head">
        <div className="ac-transcript-title">{first.detail?.question || first.title || 'Conversation'}</div>
        <div className="ac-transcript-meta">
          <button type="button" className="ac-person-link" onClick={() => onPerson?.(first.user_id)}>
            <Avatar user={user} size={22} /> {first.username}
          </button>
          <span className="ac-pill strong">{areaLabel(first.area)}</span>
          {ds?.data_set && <span className="ac-pill"><FontAwesomeIcon icon={faDatabase} /> {ds.data_set}{ds.variant && ds.variant !== 'mh' && ds.variant !== 'ppi' ? ` · ${ds.variant}` : ''}</span>}
          <span>{dateTime(first.at)}</span>
          <span>{turns.length} {turns.length === 1 ? 'question' : 'questions'}</span>
          <span><FontAwesomeIcon icon={faClock} /> {duration(total)}</span>
          {tokens > 0 && <span><FontAwesomeIcon icon={faBolt} /> {num(tokens)}</span>}
        </div>
      </header>
      <div className="ac-turns">
        {turns.map((e) => <Turn key={e.id} e={e} />)}
        {side.map((e) => {
          const s = describe({ ...e, doc_type: e.detail?.type });
          return (
            <div key={e.id} className="ac-side">
              <FontAwesomeIcon icon={CATEGORY[e.category]?.icon} /> {s.verb} <b>{s.object}</b> · {timeOf(e.at)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function EventDetail({ client, eventId, onPerson, onThread }) {
  const [e, setE] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let off = false;
    client.get(`/api/admin/activity/events/${eventId}`)
      .then((v) => { if (!off) setE(v); })
      .catch((err) => { if (!off) setError(err.message); });
    return () => { off = true; };
  }, [client, eventId]);
  if (error) return <div className="ac-empty">{error}</div>;
  if (!e) return <div className="ac-empty"><span className="ac-spinner" /></div>;
  const d = e.detail || {};
  const s = describe({ ...e, doc_type: d.type, reason: d.reason, seconds: d.seconds, data_set: d.data_set });
  const rows = [
    ['When', dateTime(e.at)],
    ['Where', areaLabel(e.area)],
    ['Status', e.status === 'error' ? 'failed' : e.status === 'aborted' ? 'broken off before the answer was complete' : 'ok'],
    e.duration_ms != null && ['Duration', duration(e.duration_ms)],
    d.seconds != null && ['Listened', `${d.seconds} s of ${d.duration || '?'} s${d.completed ? ' (to the end)' : ''}`],
    d.length && ['Version', d.length === 'quick_info' ? 'short' : 'long'],
    d.handover && ['Handed over to', d.handover],
    d.reason && ['Reason', d.reason.replace(/_/g, ' ')],
    d.type && ['Type', docLabel(d.type)],
    d.question && ['From the question', d.question],
    d.data_set && ['Data set', d.data_set],
    d.agent && ['Device', d.agent],
    d.ticker && ['Ticker', d.ticker],
    d.positioning && ['Positioning', d.positioning],
    d.backfilled && ['Source', 'recorded before the Activity log existed'],
  ].filter(Boolean);
  return (
    <div className="ac-event-detail">
      <div className="ac-event-head">
        <span className={`ac-icon ac-cat-${e.category}`}><FontAwesomeIcon icon={CATEGORY[e.category]?.icon} /></span>
        <div>
          <button type="button" className="ac-person-link" onClick={() => onPerson?.(e.user_id)}>{e.username || 'unknown'}</button>{' '}
          {s.verb}
          {s.object && <div className="ac-event-object">{s.object}</div>}
        </div>
      </div>
      <dl className="ac-kv">
        {rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
      </dl>
      {e.thread && <button type="button" className="ac-btn ghost" onClick={() => onThread?.(e.thread)}>Open the conversation</button>}
    </div>
  );
}

export const userName = (u) => fullName(u) || u?.username || '';
